import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

interface Organization {
  id: string;
  name: string;
  plan_name: string;
  subscription_status: string;
  owner_id: string;
  created_at: string;
  updated_at: string | null;
}

export const useOrganization = () => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/v1/organizations/current');
        if (!response.ok) {
          throw new Error('Failed to fetch organization');
        }
        const data = await response.json();
        setOrganization(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setOrganization(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [user]);

  const updateOrganization = async (updates: Partial<Organization>) => {
    if (!organization) return;

    try {
      const response = await fetch(`/api/v1/organizations/${organization.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update organization');
      }

      const updatedOrg = await response.json();
      setOrganization(updatedOrg);
      setError(null);
      return updatedOrg;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  return {
    organization,
    loading,
    error,
    updateOrganization,
  };
}; 